import {Truncate} from '@dropbox/dig-components/truncate';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {StarLine} from '@dropbox/dig-icons/assets';
import {StatusButtonIcon} from 'components/DSYS/StatusButtonIcon';

import {MetricValue, ProgressMeta} from './ProgressRow';
import type {ProgressItem} from './scanProgressItems';

// The number first, then what it measures:
//
//   ●  $2.8M          Drive Incremental Q2 NNARR – Budget
//      of 444 goal    ☆ North star · Q4 · Lagging · Shahar Marom
const Headline = ({item}: {item: ProgressItem}) => (
  <Box display="flex" alignItems="flex-start" style={{gap: 12, minWidth: 0}}>
    {/* 6px drops the 16px icon onto the number's (28px) line. */}
    <Box style={{flexShrink: 0, paddingTop: 6}}>
      <StatusButtonIcon status={item.status} size="small" includeArchived />
    </Box>
    <MetricValue value={item.value} large />
    {/* 4px centers the title's (20px) line on the number's. */}
    <Box style={{flex: 1, minWidth: 0, paddingTop: 4}}>
      <Text size="small" tagName="div">
        <Truncate lines={2}>{item.title}</Truncate>
      </Text>
      <ProgressMeta item={item} />
    </Box>
  </Box>
);

// Top-set cards only: leadership expects one here. The pencil opens the metrics
// modal, where an owner stars one.
const Missing = () => (
  <Box display="flex" alignItems="center" style={{gap: 8}}>
    <UIIcon src={StarLine} size="small" className={atoms({color: 'Warning Base'})} />
    <Text size="small" isBold>
      No north star
    </Text>
    <Text size="xsmall" color="faint">
      Top workstreams should star one metric
    </Text>
  </Box>
);

/**
 * /workstreams: the open card's north star, in a tinted box of its
 * own so it reads as the headline without a heading. Same border and padding as
 * the Metrics & milestones box below, so its number and name line up with the
 * Metrics rows there. Read-only: the north star edits on the detail page.
 */
export const NorthStarBlock = ({
  // Undefined: the top set's missing north star.
  item,
}: {
  item?: ProgressItem;
}) => (
  <Box
    backgroundColor="Primary Surface"
    borderStyle="Solid"
    borderWidth="1"
    borderColor="Border Subtle"
    borderRadius="Medium"
    padding="16"
    style={{minWidth: 0}}
  >
    {item ? <Headline item={item} /> : <Missing />}
  </Box>
);
